import { compare, compareAnalyze } from '../../../../../webmodule-common/other/clone';
import { FinalProcessItem, ItemContainerCompareResult, OutOfDateFailure } from './out-of-date-quote-item-handler';
import { FranchiseeQuoteContainerManager } from '../franchisee-quote-manager';
import {
  getV6FrameForContainer,
  getV6QuoteItemForContainer,
  getV6SupplierForContainer,
  isV6,
  quoteItemAssets,
  updateQuoteItemContainer
} from '../../../../quotes/data/v6/helper-functions';
import { information } from '../../../../../webmodule-common/other/ui/modal-option';
import { interpolateExternal } from '../../../../../localization/global';
import { isSupplierFrameItem, processQuoteItemState } from '../../../../quotes/data/quote-helper-functions';
import { LangOutOfDateQuoteItemHandlerSafeMode } from '../../../../../localization/scripts/dealer-franchisee/quotes/data/v6/out-of-date-quote-item-handler-safe-mode-lang';
import { Message, MessageType } from '../../../../../webmodule-common/other/messages';
import {
  OutOfDateQuoteItemHandler,
  QuoteItemProcessingCompleteItem
} from '../../../../quotes/views/quote-item-validation-modal';
import { QuoteContainerManager } from '../../../../quotes/data/quote-container';
import {
  compareFrames,
  QuoteFrameProcessingDataProvider
} from '../../../../../webmodule-common/interop/webmodule-interop';
import { QuoteItemContainer } from '../../../../quotes/data/quote-item-container';
import { showDevelopmentError } from '../../../../../webmodule-common/other/development-error';
import { toJsonStr } from '../../../../../webmodule-common/other/blob-converters';
import { v6Util } from '../../../../../webmodule-common/interop/interfaces/v6-config';
import { v6Config } from '../../../../../webmodule-common/interop/interfaces/v6-config';
import { V6FranchiseeQuoteProviderData } from '../franchisee-quote-provider-data';
import { QuoteItemAssetType } from '../../../../api/quote-api';
import { AttachedBlobAsset } from '../../../../api/dealer-api-interface-all';

/***
 * only update items if they have not changed.
 */
export class V6OutOfDateQuoteItemHandlerSafeMode implements OutOfDateQuoteItemHandler {
  failedItems: OutOfDateFailure[] = [];

  private finalStageItems: FinalProcessItem[] = [];
  allowStructureChange: boolean;

  qcm: FranchiseeQuoteContainerManager;
  assets: { [key: string]: Partial<Record<QuoteItemAssetType, AttachedBlobAsset>> } = {};

  constructor(qcm: FranchiseeQuoteContainerManager) {
    this.allowStructureChange = false;
    this.qcm = qcm;
  }

  isOutOfDate(qic: QuoteItemContainer): boolean {
    return this.qcm.isFrameOutOfDate(qic, [], false);
  }

  isValidatingItem(qic: QuoteItemContainer): boolean {
    return isV6(qic) && isSupplierFrameItem(qic.item);
  }

  displayInfo(qic: QuoteItemContainer): string {
    const si = getV6SupplierForContainer(qic);
    return si?.quoteItemVersion ?? 'N/A';
  }

  async finalProcessing(qm: QuoteContainerManager): Promise<boolean> {
    if (this.failedItems.length === 0) {
      for (const finalItem of this.finalStageItems) {
        const itemContainer = finalItem.qic;
        const completeItem = finalItem.completeItem;
        const dataVer = itemContainer.data?.recordVersion;
        const newContainer = await qm.saveAndUpdateQuoteItem(
          itemContainer,
          this.assets[itemContainer.item.id],
          undefined,
          true
        );
        itemContainer.item = newContainer.item;
        itemContainer.data = newContainer.data;
        itemContainer.price = newContainer.price;
        itemContainer.buyInData = newContainer.buyInData;
        if (dataVer === newContainer.data?.recordVersion) {
          completeItem.completionStatus = processQuoteItemState.errors;
          completeItem.error = LangOutOfDateQuoteItemHandlerSafeMode.couldNotBeSaved();
          await information(
            LangOutOfDateQuoteItemHandlerSafeMode.itemCouldNotBeSaved({
              position: qm.itemPosition(itemContainer.item.id)!
            })
          );
          return false;
        }
      }
      return true;
    }
    await information(this.combineErrors(qm), LangOutOfDateQuoteItemHandlerSafeMode.errorRevalidating());
    return false;
  }

  combineErrors(qm: QuoteContainerManager): string {
    let result = '';
    this.failedItems.forEach(x => {
      result += LangOutOfDateQuoteItemHandlerSafeMode.itemReason({
        position: qm.itemPosition(x.qic.item.id)!,
        reason: x.reason
      });
    });
    return result;
  }

  async processItemEvent(
    qm: QuoteContainerManager,
    itemContainer: QuoteItemContainer,
    completeItem: QuoteItemProcessingCompleteItem,
    progress: (caption: string) => Promise<void>
  ): Promise<void> {
    //get the v6QuoteItem that was placed in memory that maps to the ui.
    const originalV6QuoteItemStorage = getV6QuoteItemForContainer(itemContainer);
    if (!originalV6QuoteItemStorage) {
      //should never happen.
      await showDevelopmentError('quote-completedItem-validation-modal.ts, v6QuoteItem is null');
      return;
    }

    const originalItemContainer = qm.cloneItemContainer(itemContainer);

    const data = qm.quoteProviderData() as V6FranchiseeQuoteProviderData;
    const input: QuoteFrameProcessingDataProvider = {
      supplierId: qm.quote.supplierId,
      supplierReferenceOverrideKey: qm.quote.quoteOwnerId,
      quantity: itemContainer.item.quantity,
      quoteDefaults: data.defaultOptions,
      quoteIGUs: data.igu,
      quoteItem: originalV6QuoteItemStorage,
      progress: progress
    };
    const result = await v6Config().processV6QuoteItem(input, false);

    if (result) {
      //    const thumb = result.thumbnail ?? '';

      //in safe mode, we are never updating buyin information
      //we may want to pull buyin data at the v6 only level to compare for structural change
      //current design doesn't allow this as its already merged.
      //const buyInCost = await qm.getBuyInCosts(result.quoteItem['buyIn']);
      const newContainer = qm.cloneItemContainer(itemContainer);
      const v6QuoteItemStorage = toJsonStr(result.quoteItem) ?? null;

      //NOTE:- we are only updating the data, and not thumbnail, price, buyin etc. because this is a safe mode run to check for structure changes only.
      // and the update is purely expecting to update the version information.
      this.assets[itemContainer.item.id] = quoteItemAssets({
        data: v6QuoteItemStorage
      });
      updateQuoteItemContainer(
        newContainer,
        v6QuoteItemStorage,
        /*    result.price 
                //do not update the price in the safe mode. this handler is looking to update versions for structural integrity only
                */
        null,
        undefined
      );

      completeItem.newPrice = newContainer.price.quantityCost;

      const quoteItem = getV6QuoteItemForContainer(originalItemContainer);
      const originalFrameData = quoteItem ? v6Util().getFrameData(quoteItem) : null;
      const newFrameData = result.quoteItem ? v6Util().getFrameData(result.quoteItem) : null;
      completeItem.frameCompareReport =
        compareFrames(originalFrameData, newFrameData, { excludeHidden: true }) ?? undefined;

      await progress(processQuoteItemState.finalizing);

      const validations = getV6FrameForContainer(newContainer)?.validations;
      completeItem.validation =
        validations?.map(x => {
          const m: Message = {
            message: interpolateExternal(x.text),
            messageType: MessageType.Warning
          };
          return m;
        }) ?? [];

      const compareResult = this.compareItems(originalItemContainer, newContainer);
      if ((!compareResult.structureChanged || this.allowStructureChange) && !compareResult.validationsChanged) {
        this.finalStageItems.push({
          qic: newContainer,
          completeItem: completeItem
        });
        completeItem.completionStatus = processQuoteItemState.completed;
        await progress(processQuoteItemState.completed);
      }
    } else await progress(processQuoteItemState.errors);
  }

  compareItems(
    originalItemContainer: QuoteItemContainer,
    itemContainer: QuoteItemContainer
  ): ItemContainerCompareResult {
    const originalData = getV6FrameForContainer(originalItemContainer);
    const newData = getV6FrameForContainer(itemContainer);

    const result: ItemContainerCompareResult = {
      structureChanged: false,
      validationsChanged: false
    };
    if (!compare(originalData?.validations, newData?.validations)) {
      result.validationsChanged = true;

      this.failedItems.push({
        qic: originalItemContainer,
        reason: LangOutOfDateQuoteItemHandlerSafeMode.newValidationsDontMatch()
      });
      if (originalData) originalData.validations = [];
      if (newData) newData.validations = [];
    }

    if (!compare(originalData, newData)) {
      if (globalThis?.isDevelopmentMode) {
        console.debug('Item Frame Changes Found');
        console.debug(compareAnalyze(originalData, newData));
        console.debug(originalData, newData);
      }

      result.structureChanged = true;
      if (!this.allowStructureChange)
        this.failedItems.push({
          qic: originalItemContainer,
          reason: LangOutOfDateQuoteItemHandlerSafeMode.frameDesignChanged()
        });
    }
    return result;
  }

  get displayInfoTitle(): string {
    return LangOutOfDateQuoteItemHandlerSafeMode.version();
  }
}
